"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import InputDataContent from "../inputDataContent";
import { cn } from "@/lib";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { useRef } from "react";
import { IRawPurchasedCompanyInfoInquiryResData } from "@/interfaces/inquiries/legal";

const CompanyInfoInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCompanyInfoInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const {
    activity,
    address,
    addressInfo,
    name,
    nationalCode,
    postalCode,
    registerDate,
    registerNumber,
    signatureAuthority,
    state,
    stateCode,
    totalStock,
    type,
    registrationUnitTitle,
  } = data.data.data;
  const { national_code } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("companyInformationInquiry.nationalId"),
      value: national_code,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("companyInformationInquiry.name"),
      value: name || "-",
    },
    {
      label: t("companyInformationInquiry.nationalId"),
      value: nationalCode || "-",
    },
    {
      label: t("companyInformationInquiry.registerNumber"),
      value: registerNumber || "-",
    },
    {
      label: t("companyInformationInquiry.postalCode"),
      value: postalCode || "-",
    },
    {
      label: t("companyInformationInquiry.registerDate"),
      value: registerDate || "-",
    },
    {
      label: t("companyInformationInquiry.city"),
      value: addressInfo?.city || "-",
    },
    {
      label: t("companyInformationInquiry.province"),
      value: addressInfo?.province || "-",
    },
    {
      label: t("companyInformationInquiry.state"),
      value: state || "-",
    },
    {
      label: t("companyInformationInquiry.totalStock"),
      value: totalStock || "-",
    },
    {
      label: t("companyInformationInquiry.type"),
      value: type || "-",
    },
    {
      label: t("companyInformationInquiry.registrationUnitTitle"),
      value: registrationUnitTitle || "-",
    },
    {
      label: t("companyInformationInquiry.address"),
      value: address || "-",
    },
    {
      label: t("companyInformationInquiry.signatureAuthority"),
      value: signatureAuthority || "-",
    },
    {
      label: t("companyInformationInquiry.activity"),
      value: activity || "-",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-sunset-gold relative max-w-[900px] rounded-2xl bg-linear-to-b to-transparent p-[2px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("companyInformationInquiry.title")}
        </h2>
        <FlexCenter className={cn("block w-full md:flex")}>
          <InputDataContent
            className={cn("md:basis-1/2")}
            content={inputContent}
          />
        </FlexCenter>
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("companyInformationInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-info-inquiry-download-pdf"
            resultContent={resultContent}
            fileName={`گزارش اطلاعات شرکت ${national_code}`}
            tableTitle={`گزارش اطلاعات شرکت ${national_code}`}
          />
        </FlexBetween>
        <div className="shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8 md:gap-8">
          {resultContent.map((item, index) => (
            <li
              key={index}
              className={cn(
                `flex-between flex items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base`,
                item.value === "-" ? "hidden" : "flex",
              )}
            >
              <span className="min-w-max">{item.label}</span>
              <p className="text-primary-main text-justify">{item.value}</p>
            </li>
          ))}
        </div>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-info-inquiry-download-pdf"
            resultContent={resultContent}
            fileName={`گزارش اطلاعات شرکت ${national_code}`}
            tableTitle={`گزارش اطلاعات شرکت ${national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-company-info-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-company-info-inquiry-new-request"
              onClick={() =>
                router.push(
                  routes.services.Company_Information_Inquiry.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle={`گزارش اطلاعات شرکت ${national_code}`}
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default CompanyInfoInquiryDetails;
